import {
  ApolloClient,
  from,
  HttpLink,
  ApolloProvider,
  PossibleTypesMap,
  InMemoryCache,
} from '@apollo/client';
import * as Sentry from '@sentry/react';
import _ from 'lodash';
import React from 'react';

import { baseAuthMiddleware } from './middleware/baseAuthMiddleware';
import { errorHandlerMiddleware } from './middleware/errorHandlerMiddleware';
import { setHeadersMiddleware } from './middleware/setHeadersMiddleware';
import { useUserAuthUnsafe } from '~/contexts/UserAuthProvider';

// Helpers
/**
 * Prevent Apollo Client from displaying arbitrary 500 messages, code taken from github issue:
 * https://github.com/apollographql/apollo-feature-requests/issues/153#issuecomment-476832408
 */
const baseCustomFetch = (uri: string, options: any) => {
  return fetch(uri, options).then((response) => {
    // This is intended only to handle cases of server timeouts that usually come from Cloudfront
    if (response.status > 500) {
      Sentry.captureException(response);
      return Promise.reject(
        new Error(
          'Unexpected server error - please refresh page and retry. Contact support if this persists.',
        ),
      );
    }
    return response;
  });
};

const baseInMemoryCache = (possibleTypes: PossibleTypesMap) => {
  return new InMemoryCache({
    possibleTypes,
    typePolicies: {
      Query: {
        fields: {
          incompleteDeliverables: {
            merge(_exisiting, incoming) {
              return [...incoming];
            },
          },
          pastDeliverables: {
            merge(_exisiting, incoming) {
              return [...incoming];
            },
          },
        },
      },
      BankDataProviderInstitution: {
        keyFields: ['dataProvider', 'bankInstitutionId'],
      },
      DeliverableItemWithTasks: {
        keyFields: ['deliverableItem', ['id']],
      },
    },
  });
};

// Exports
type Options = {
  children: React.ReactNode;
  headers?: Record<string, string>;
  possibleTypes: PossibleTypesMap;
  uri: string;
};
export const ApolloClientProvider = ({
  children,
  headers,
  possibleTypes,
  uri,
}: Options) => {
  const { accessToken } = useUserAuthUnsafe();
  const [stableHeaders, setStableHeaders] = React.useState(headers);
  React.useEffect(() => {
    setStableHeaders((prevHeaders) => {
      if (!_.isEqual(prevHeaders, headers)) return headers;
      return prevHeaders;
    });
  }, [headers]);
  const apolloClient = React.useMemo(() => {
    const cache = baseInMemoryCache(possibleTypes);
    const link = from([
      baseAuthMiddleware(accessToken?.raw),
      errorHandlerMiddleware(),
      setHeadersMiddleware({ headers: stableHeaders }),
      new HttpLink({ uri, fetch: baseCustomFetch }),
    ]);

    return new ApolloClient({ cache, link, credentials: 'include' });
  }, [accessToken?.raw, stableHeaders, possibleTypes, uri]);

  return <ApolloProvider client={apolloClient}>{children}</ApolloProvider>;
};
